import { LitElement, html, css, unsafeCSS } from 'lit'
import { customElement, state } from 'lit/decorators.js'
import { gql } from '@apollo/client'
import { apolloClient } from '../utils/apollo.js'
import { t, getCurrentLanguageId } from '../utils/i18n.js'
import type { WordGroup, ColumnConfig, Spelling } from '../utils/word-view-data-manager.js'
import '../components/word-entries-list.js'
import '../components/etymology-root-display.js'
import '../components/meaning-grammeme-display.js'
import '../components/meaning-grammeme-form.js'
import styles from './create-entry-page.css?inline'
import '@material/web/button/filled-button.js'
import '@material/web/button/outlined-button.js'
import '@material/web/textfield/filled-text-field.js'
import '@material/web/select/filled-select.js'
import '@material/web/select/select-option.js'

const CREATE_WORD_MUTATION = gql`
  mutation CreateWord {
    createWord {
      id
    }
  }
`

const CREATE_MEANING_MUTATION = gql`
  mutation CreateMeaning($typeId: ID!, $wordId: ID!, $languageId: ID!) {
    createMeaning(input: {
      typeId: $typeId
      wordId: $wordId
      languageId: $languageId
    }) {
      id
    }
  }
`

const CREATE_SPELLING_MUTATION = gql`
  mutation CreateSpelling($spelling: String!, $systemId: ID!, $meaningId: ID!) {
    createSpelling(input: {
      spelling: $spelling
      systemId: $systemId
      meaningId: $meaningId
    }) {
      id
    }
  }
`

const GET_LANGUAGES_QUERY = gql`
  query GetLanguages {
    languages {
      id
      name
    }
  }
`

const GET_MEANING_TYPES_QUERY = gql`
  query GetMeaningTypes {
    meaningTypes {
      id
      name
    }
  }
`

const GET_SPELLING_SYSTEMS_QUERY = gql`
  query GetSpellingSystems {
    spellingSystems {
      id
      name
    }
  }
`

const GET_RECENT_SPELLINGS_QUERY = gql`
  query GetRecentSpellings($limit: Int, $sinceHours: Int) {
    recentSpellings(limit: $limit, sinceHours: $sinceHours) {
      id
      spelling
      system { id name }
      meaning {
        id
        language { id name }
        word { id }
        type { id name }
      }
    }
  }
`

const SEARCH_MEANINGS_QUERY = gql`
  query SearchMeanings($filter: String, $languageId: ID, $limit: Int, $offset: Int, $displayLanguageId: ID) {
    searchMeanings(filter: $filter, languageId: $languageId, limit: $limit, offset: $offset, displayLanguageId: $displayLanguageId) {
      meaning {
        id
        type {
          id
          name
        }
        word {
          id
        }
        language {
          id
          name
        }
      }
      primarySpelling
      displaySpelling
    }
  }
`

const CREATE_ETYMOLOGY_MUTATION = gql`
  mutation CreateEtymology($meaningId: ID!, $title: String, $description: String) {
    createEtymology(input: { meaningId: $meaningId, title: $title, description: $description }) {
      id
    }
  }
`

const CREATE_ETYMOLOGY_ROOT_MUTATION = gql`
  mutation CreateEtymologyRoot($etymologyId: ID!, $rootId: ID!) {
    createEtymologyRoot(input: { etymologyId: $etymologyId, rootId: $rootId }) {
      id
    }
  }
`

const MEANING_GRAMMEMES_QUERY = gql`
  query MeaningGrammemes($meaningId: ID!) {
    meaningGrammemes(meaningId: $meaningId) {
      id
      meaning { id }
      grammeme { id value feature { id name } }
    }
  }
`

const DELETE_MEANING_GRAMMEME_MUTATION = gql`
  mutation DeleteMeaningGrammeme($id: ID!) {
    deleteMeaningGrammeme(id: $id)
  }
`

type Mode = 'word-meaning-spelling' | 'meaning-spelling' | 'spelling'

interface SpellingRow {
  id: string
  systemId: string
  text: string
}

@customElement('create-entry-page')
export class CreateEntryPage extends LitElement {
  @state() private mode: Mode = 'word-meaning-spelling'
  @state() private languages: Array<{ id: string; name: string }> = []
  @state() private meaningTypes: Array<{ id: string; name: string }> = []
  @state() private spellingSystems: Array<{ id: string; name: string }> = []
  // Spelling mode: meaning via paginated search (language + filter)
  @state() private spellingSearchLanguageId = ''
  @state() private spellingSearchFilter = ''
  @state() private spellingSearchResults: Array<{ meaning: { id: string; type: { id: string; name: string }; word: { id: string }; language: { id: string; name: string } }; primarySpelling: string | null; displaySpelling: string | null }> = []
  @state() private spellingSearchLoading = false
  @state() private spellingSearchPage = 0
  @state() private selectedMeaningFromSearch: { meaning: { id: string; type: { name: string }; language: { name: string } }; primarySpelling: string | null; displaySpelling: string | null } | null = null
  @state() private loading = false
  @state() private creating = false
  @state() private error = ''
  @state() private success = false

  // Form state
  @state() private selectedWordId = ''
  @state() private selectedMeaningId = ''
  @state() private selectedMeaningTypeId = ''
  @state() private selectedLanguageId = ''
  @state() private spellings: SpellingRow[] = []
  @state() private etymologyRoots: Array<{ id: string; typeName: string; languageName: string; displaySpelling: string }> = []
  @state() private etymologySearchLanguageId = ''
  @state() private etymologySearchFilter = ''
  @state() private etymologySearchResults: Array<{ meaning: { id: string; type: { id: string; name: string }; word: { id: string }; language: { id: string; name: string } }; primarySpelling: string | null; displaySpelling: string | null }> = []
  @state() private etymologySearchLoading = false
  @state() private etymologySearchPage = 0
  private static readonly ETYMOLOGY_SEARCH_PAGE_SIZE = 10
  // Meaning-spelling mode: word via meaning search (language + filter, limit 5)
  @state() private wordSearchLanguageId = ''
  @state() private wordSearchFilter = ''
  @state() private wordSearchResults: Array<{ meaning: { id: string; type: { id: string; name: string }; word: { id: string }; language: { id: string; name: string } }; primarySpelling: string | null; displaySpelling: string | null }> = []
  @state() private wordSearchLoading = false
  @state() private wordSearchPage = 0
  @state() private selectedWordFromSearch: { meaning: { id: string; word: { id: string }; type: { name: string }; language: { name: string } }; primarySpelling: string | null; displaySpelling: string | null } | null = null
  @state() private recentEntries: WordGroup[] = []
  @state() private recentEntriesLoading = false
  // After creating a meaning (word-meaning-spelling or meaning-spelling), show grammeme form for it
  @state() private lastCreatedMeaningId = ''
  @state() private lastCreatedLanguageId = ''
  @state() private meaningGrammemes: Array<{ id: string; grammeme: { id: string; value: string; feature: { id: string; name: string } } }> = []
  @state() private deletingGrammemeId = ''
  private _spellingRowId = 0
  private _etymologySearchDebounce: ReturnType<typeof setTimeout> | null = null
  private _wordSearchDebounce: ReturnType<typeof setTimeout> | null = null
  private _spellingSearchDebounce: ReturnType<typeof setTimeout> | null = null

  static styles = css`${unsafeCSS(styles)}`

  private _triggerSubmitHandler = () => { this.handleCreate() }

  async connectedCallback() {
    super.connectedCallback()
    await this.loadData()
    this.loadRecentEntries()
    // E2E: allow triggering submit without relying on form requestSubmit (CI flakiness with custom elements)
    this.addEventListener('create-entry-trigger-submit', this._triggerSubmitHandler)
    // Listen for i18n updates
    window.addEventListener('i18n-updated', () => {
      this.requestUpdate()
    })
  }

  disconnectedCallback() {
    this.removeEventListener('create-entry-trigger-submit', this._triggerSubmitHandler)
    super.disconnectedCallback()
  }

  updated(changedProperties: Map<string, unknown>) {
    super.updated?.(changedProperties)
    // When page loads and we have systems, ensure at least one spelling row
    if (!this.loading && this.spellingSystems.length && this.spellings.length === 0) {
      this.spellings = [{ id: `row-${++this._spellingRowId}`, systemId: this.spellingSystems[0].id, text: '' }]
    }
  }

  async loadData() {
    this.loading = true
    this.error = ''
    try {
      const [languagesResult, meaningTypesResult, spellingSystemsResult] = await Promise.all([
        apolloClient.query({ query: GET_LANGUAGES_QUERY }),
        apolloClient.query({ query: GET_MEANING_TYPES_QUERY }),
        apolloClient.query({ query: GET_SPELLING_SYSTEMS_QUERY }),
      ])

      this.languages = (languagesResult.data as any).languages ?? []
      this.meaningTypes = (meaningTypesResult.data as any).meaningTypes ?? []
      this.spellingSystems = (spellingSystemsResult.data as any).spellingSystems ?? []
    } catch (err: any) {
      this.error = err.message || t('createDialog.error')
    } finally {
      this.loading = false
    }
  }

  private static groupSpellingsByWord(spellings: Spelling[]): WordGroup[] {
    const byWord = new Map<string, Spelling[]>()
    for (const s of spellings) {
      const wordId = s.meaning?.word?.id ?? s.id
      if (!byWord.has(wordId)) byWord.set(wordId, [])
      byWord.get(wordId)!.push(s)
    }
    return Array.from(byWord.entries()).map(([wordId, spellings]) => ({ wordId, spellings }))
  }

  private async loadRecentEntries() {
    this.recentEntriesLoading = true
    this.recentEntries = []
    try {
      const result = await apolloClient.query({
        query: GET_RECENT_SPELLINGS_QUERY,
        variables: { limit: 10, sinceHours: 24 },
        fetchPolicy: 'network-only',
      })
      const spellings = (result.data as any).recentSpellings ?? []
      this.recentEntries = CreateEntryPage.groupSpellingsByWord(spellings as Spelling[])
    } catch (_) {
      this.recentEntries = []
    } finally {
      this.recentEntriesLoading = false
    }
  }

  private resetForm() {
    this.selectedWordId = ''
    this.selectedMeaningId = ''
    this.selectedMeaningTypeId = ''
    this.selectedLanguageId = ''
    this.etymologyRoots = []
    this.etymologySearchLanguageId = ''
    this.etymologySearchFilter = ''
    this.etymologySearchResults = []
    this.etymologySearchPage = 0
    this.wordSearchLanguageId = ''
    this.wordSearchFilter = ''
    this.wordSearchResults = []
    this.wordSearchPage = 0
    this.selectedWordFromSearch = null
    this.spellingSearchLanguageId = ''
    this.lastCreatedMeaningId = ''
    this.lastCreatedLanguageId = ''
    this.meaningGrammemes = []
    this.spellingSearchFilter = ''
    this.spellingSearchResults = []
    this.spellingSearchPage = 0
    this.selectedMeaningFromSearch = null
    this.error = ''
    this.success = false
    // One default spelling row when we have systems
    this.spellings = this.spellingSystems.length
      ? [{ id: `row-${++this._spellingRowId}`, systemId: this.spellingSystems[0].id, text: '' }]
      : []
  }

  private addSpellingRow() {
    const firstSystem = this.spellingSystems[0]?.id ?? ''
    this.spellings = [...this.spellings, { id: `row-${++this._spellingRowId}`, systemId: firstSystem, text: '' }]
  }

  private removeSpellingRow(id: string) {
    this.spellings = this.spellings.filter((r) => r.id !== id)
  }

  private updateSpellingRow(id: string, field: 'systemId' | 'text', value: string) {
    this.spellings = this.spellings.map((r) => (r.id === id ? { ...r, [field]: value } : r))
  }

  private addEtymologyRoot(item: { meaning: { id: string; type: { name: string }; language: { name: string } }; primarySpelling?: string | null; displaySpelling?: string | null }) {
    const id = item.meaning.id
    if (this.etymologyRoots.some((r) => r.id === id)) return
    const spelling = item.displaySpelling ?? item.primarySpelling ?? ''
    this.etymologyRoots = [
      ...this.etymologyRoots,
      {
        id,
        typeName: item.meaning.type.name,
        languageName: item.meaning.language.name,
        displaySpelling: spelling,
      },
    ]
  }

  private async searchEtymologyMeanings(page = 0) {
    if (!this.etymologySearchLanguageId || this.etymologySearchFilter.trim().length === 0) {
      this.etymologySearchResults = []
      this.etymologySearchPage = 0
      return
    }
    this.etymologySearchLoading = true
    this.etymologySearchResults = []
    try {
      const result = await apolloClient.query({
        query: SEARCH_MEANINGS_QUERY,
        variables: {
          filter: this.etymologySearchFilter.trim() || null,
          languageId: this.etymologySearchLanguageId || null,
          limit: CreateEntryPage.ETYMOLOGY_SEARCH_PAGE_SIZE,
          offset: page * CreateEntryPage.ETYMOLOGY_SEARCH_PAGE_SIZE,
          displayLanguageId: getCurrentLanguageId() || null,
        },
      })
      this.etymologySearchResults = (result.data as any).searchMeanings ?? []
      this.etymologySearchPage = page
    } catch (_) {
      this.etymologySearchResults = []
    } finally {
      this.etymologySearchLoading = false
    }
  }

  private onEtymologySearchInput() {
    if (this._etymologySearchDebounce) clearTimeout(this._etymologySearchDebounce)
    this.etymologySearchPage = 0
    this._etymologySearchDebounce = setTimeout(() => this.searchEtymologyMeanings(0), 300)
  }

  private removeEtymologyRoot(index: number) {
    this.etymologyRoots = this.etymologyRoots.filter((_, i) => i !== index)
  }

  private async searchWordMeanings(page = 0) {
    if (!this.wordSearchLanguageId || this.wordSearchFilter.trim().length === 0) {
      this.wordSearchResults = []
      this.wordSearchPage = 0
      return
    }
    this.wordSearchLoading = true
    this.wordSearchResults = []
    try {
      const result = await apolloClient.query({
        query: SEARCH_MEANINGS_QUERY,
        variables: {
          filter: this.wordSearchFilter.trim() || null,
          languageId: this.wordSearchLanguageId || null,
          limit: CreateEntryPage.ETYMOLOGY_SEARCH_PAGE_SIZE,
          offset: page * CreateEntryPage.ETYMOLOGY_SEARCH_PAGE_SIZE,
          displayLanguageId: getCurrentLanguageId() || null,
        },
      })
      this.wordSearchResults = (result.data as any).searchMeanings ?? []
      this.wordSearchPage = page
    } catch (_) {
      this.wordSearchResults = []
    } finally {
      this.wordSearchLoading = false
    }
  }

  private onWordSearchInput() {
    if (this._wordSearchDebounce) clearTimeout(this._wordSearchDebounce)
    this.wordSearchPage = 0
    this._wordSearchDebounce = setTimeout(() => this.searchWordMeanings(0), 300)
  }

  private selectWordFromSearch(item: { meaning: { id: string; word: { id: string }; type: { id: string; name: string }; language: { id: string; name: string } }; primarySpelling: string | null; displaySpelling: string | null }) {
    this.selectedWordFromSearch = item
    this.selectedWordId = item.meaning.word.id
    // Pre-fill meaning type and language from the selected word's meaning (user can change if adding a different type/language)
    this.selectedMeaningTypeId = item.meaning.type?.id ?? ''
    this.selectedLanguageId = item.meaning.language?.id ?? ''
  }

  private clearSelectedWord() {
    this.selectedWordFromSearch = null
    this.selectedWordId = ''
  }

  private async searchMeaningsForSpelling(page = 0) {
    if (!this.spellingSearchLanguageId || this.spellingSearchFilter.trim().length === 0) {
      this.spellingSearchResults = []
      this.spellingSearchPage = 0
      return
    }
    this.spellingSearchLoading = true
    this.spellingSearchResults = []
    try {
      const result = await apolloClient.query({
        query: SEARCH_MEANINGS_QUERY,
        variables: {
          filter: this.spellingSearchFilter.trim() || null,
          languageId: this.spellingSearchLanguageId || null,
          limit: CreateEntryPage.ETYMOLOGY_SEARCH_PAGE_SIZE,
          offset: page * CreateEntryPage.ETYMOLOGY_SEARCH_PAGE_SIZE,
          displayLanguageId: getCurrentLanguageId() || null,
        },
      })
      this.spellingSearchResults = (result.data as any).searchMeanings ?? []
      this.spellingSearchPage = page
    } catch (_) {
      this.spellingSearchResults = []
    } finally {
      this.spellingSearchLoading = false
    }
  }

  private onSpellingSearchInput() {
    if (this._spellingSearchDebounce) clearTimeout(this._spellingSearchDebounce)
    this.spellingSearchPage = 0
    this._spellingSearchDebounce = setTimeout(() => this.searchMeaningsForSpelling(0), 300)
  }

  private selectMeaningFromSearch(item: { meaning: { id: string; type: { name: string }; language: { name: string } }; primarySpelling: string | null; displaySpelling: string | null }) {
    this.selectedMeaningFromSearch = item
    this.selectedMeaningId = item.meaning.id
  }

  private clearSelectedMeaning() {
    this.selectedMeaningFromSearch = null
    this.selectedMeaningId = ''
  }

  private validSpellings(): SpellingRow[] {
    return this.spellings.filter((r) => r.systemId && r.text.trim().length > 0)
  }

  private async handleCreate() {
    this.creating = true
    this.error = ''
    this.success = false

    const valid = this.validSpellings()
    if (valid.length === 0) {
      this.error = t('createDialog.errorNoSpelling')
      this.creating = false
      return
    }

    try {
      let createdMeaningId = ''
      let createdLanguageId = ''
      if (this.mode === 'word-meaning-spelling') {
        if (!this.selectedMeaningTypeId) {
          throw new Error(t('createDialog.errorSelectMeaningType'))
        }
        if (!this.selectedLanguageId) {
          throw new Error(t('createDialog.errorSelectLanguage'))
        }
        const wordResult = await apolloClient.mutate({ mutation: CREATE_WORD_MUTATION })
        const wordId = (wordResult.data as any).createWord.id

        const meaningResult = await apolloClient.mutate({
          mutation: CREATE_MEANING_MUTATION,
          variables: {
            typeId: this.selectedMeaningTypeId,
            wordId,
            languageId: this.selectedLanguageId,
          },
        })
        const meaningId = (meaningResult.data as any).createMeaning.id
        createdMeaningId = meaningId
        createdLanguageId = this.selectedLanguageId

        for (const row of valid) {
          await apolloClient.mutate({
            mutation: CREATE_SPELLING_MUTATION,
            variables: { spelling: row.text.trim(), systemId: row.systemId, meaningId },
          })
        }

        if (this.etymologyRoots.length > 0) {
          const etymResult = await apolloClient.mutate({
            mutation: CREATE_ETYMOLOGY_MUTATION,
            variables: { meaningId, title: null, description: null },
          })
          const etymologyId = (etymResult.data as any).createEtymology.id
          for (const root of this.etymologyRoots) {
            if (root.id) {
              await apolloClient.mutate({
                mutation: CREATE_ETYMOLOGY_ROOT_MUTATION,
                variables: { etymologyId, rootId: root.id },
              })
            }
          }
        }
      } else if (this.mode === 'meaning-spelling') {
        if (!this.selectedWordId) {
          throw new Error(t('createDialog.errorSelectWord'))
        }
        if (!this.selectedMeaningTypeId) {
          throw new Error(t('createDialog.errorSelectMeaningType'))
        }
        if (!this.selectedLanguageId) {
          throw new Error(t('createDialog.errorSelectLanguage'))
        }
        const meaningResult = await apolloClient.mutate({
          mutation: CREATE_MEANING_MUTATION,
          variables: {
            typeId: this.selectedMeaningTypeId,
            wordId: this.selectedWordId,
            languageId: this.selectedLanguageId,
          },
        })
        const meaningId = (meaningResult.data as any).createMeaning.id
        createdMeaningId = meaningId
        createdLanguageId = this.selectedLanguageId

        for (const row of valid) {
          await apolloClient.mutate({
            mutation: CREATE_SPELLING_MUTATION,
            variables: { spelling: row.text.trim(), systemId: row.systemId, meaningId },
          })
        }

        if (this.etymologyRoots.length > 0) {
          const etymResult = await apolloClient.mutate({
            mutation: CREATE_ETYMOLOGY_MUTATION,
            variables: { meaningId, title: null, description: null },
          })
          const etymologyId = (etymResult.data as any).createEtymology.id
          for (const root of this.etymologyRoots) {
            if (root.id) {
              await apolloClient.mutate({
                mutation: CREATE_ETYMOLOGY_ROOT_MUTATION,
                variables: { etymologyId, rootId: root.id },
              })
            }
          }
        }
      } else if (this.mode === 'spelling') {
        if (!this.selectedMeaningId) {
          throw new Error(t('createDialog.errorSelectMeaning'))
        }
        for (const row of valid) {
          await apolloClient.mutate({
            mutation: CREATE_SPELLING_MUTATION,
            variables: {
              spelling: row.text.trim(),
              systemId: row.systemId,
              meaningId: this.selectedMeaningId,
            },
          })
        }
      }

      this.success = true
      this.resetForm()
      if (createdMeaningId && createdLanguageId) {
        this.lastCreatedMeaningId = createdMeaningId
        this.lastCreatedLanguageId = createdLanguageId
      }
      this.loadRecentEntries()
      // Keep success message visible briefly so user and E2E can see it (resetForm() clears it)
      this.success = true
      setTimeout(() => {
        this.success = false
      }, 5000)
    } catch (err: any) {
      if (err.message?.includes('authentication required') || err.message?.includes('NotAuthenticated')) {
        this.error = t('createDialog.errorNotAuthenticated')
      } else if (err.message?.includes('does not have Universal role') || err.message?.includes('NotAuthorized')) {
        this.error = t('createDialog.errorNotAuthorized')
      } else {
        this.error = err.message || t('createDialog.error')
      }
    } finally {
      this.creating = false
    }
  }

  private async refreshMeaningGrammemes() {
    if (!this.lastCreatedMeaningId) return
    try {
      const mgRes = await apolloClient.query({ 
        query: MEANING_GRAMMEMES_QUERY,
        variables: { meaningId: this.lastCreatedMeaningId }
      })
      this.meaningGrammemes = (mgRes.data as any).meaningGrammemes ?? []
    } catch {
      // ignore
    }
  }

  private async handleMeaningGrammemeRemove(e: CustomEvent<{ id: string }>) {
    const id = e.detail?.id
    if (!id) return
    this.deletingGrammemeId = id
    try {
      await apolloClient.mutate({ mutation: DELETE_MEANING_GRAMMEME_MUTATION, variables: { id } })
      this.meaningGrammemes = this.meaningGrammemes.filter((mg) => mg.id !== id)
    } finally {
      this.deletingGrammemeId = ''
    }
  }

  private handleCancel() {
    window.history.pushState({}, '', '/')
    window.dispatchEvent(new PopStateEvent('popstate'))
  }

  render() {
    return html`
      <div class="page-container">
        <h1>${t('createDialog.title')}</h1>
        
        <form novalidate data-testid="create-form" @submit=${(e: Event) => { e.preventDefault(); this.handleCreate(); }}>
          ${this.loading ? html`<div class="loading">${t('common.loading')}</div>` : ''}
          
          ${this.error ? html`<div class="error" data-testid="create-error">${this.error}</div>` : ''}
          ${this.success ? html`<div class="success" data-testid="create-success">${t('createDialog.success')}</div>` : ''}

          <div class="tab-bar" role="tablist" aria-label=${t('createDialog.title')}>
            <button
              type="button"
              role="tab"
              class="tab-button ${this.mode === 'word-meaning-spelling' ? 'selected' : ''}"
              aria-selected=${this.mode === 'word-meaning-spelling'}
              tabindex=${this.mode === 'word-meaning-spelling' ? 0 : -1}
              data-testid="create-tab-word-meaning-spelling"
              @click=${() => { this.mode = 'word-meaning-spelling'; this.resetForm(); }}
            >${t('createDialog.createWordMeaningSpelling')}</button>
            <button
              type="button"
              role="tab"
              class="tab-button ${this.mode === 'meaning-spelling' ? 'selected' : ''}"
              aria-selected=${this.mode === 'meaning-spelling'}
              tabindex=${this.mode === 'meaning-spelling' ? 0 : -1}
              data-testid="create-tab-meaning-spelling"
              @click=${() => { this.mode = 'meaning-spelling'; this.resetForm(); }}
            >${t('createDialog.createMeaningSpelling')}</button>
            <button
              type="button"
              role="tab"
              class="tab-button ${this.mode === 'spelling' ? 'selected' : ''}"
              aria-selected=${this.mode === 'spelling'}
              tabindex=${this.mode === 'spelling' ? 0 : -1}
              data-testid="create-tab-spelling"
              @click=${() => { this.mode = 'spelling'; this.resetForm(); }}
            >${t('createDialog.createSpelling')}</button>
          </div>

          <div class="form-content">
            ${this.mode === 'word-meaning-spelling' ? this.renderWordMeaningSpellingForm() : ''}
            ${this.mode === 'meaning-spelling' ? this.renderMeaningSpellingForm() : ''}
            ${this.mode === 'spelling' ? this.renderSpellingForm() : ''}
          </div>

          ${this.lastCreatedMeaningId && this.lastCreatedLanguageId ? html`
            <section class="edit-section">
              <h2 class="section-title">${t('meaningGrammemes.sectionTitle')}</h2>
              <meaning-grammeme-display
                .items=${this.meaningGrammemes}
                removable
                .deletingId=${this.deletingGrammemeId}
                @meaning-grammeme-remove=${this.handleMeaningGrammemeRemove}
              ></meaning-grammeme-display>
              <meaning-grammeme-form
                .meaningId=${this.lastCreatedMeaningId}
                .languageId=${this.lastCreatedLanguageId}
                .existingGrammemeIds=${this.meaningGrammemes.map((mg) => mg.grammeme.id)}
                .existingFeatureIds=${this.meaningGrammemes.map((mg) => mg.grammeme?.feature?.id).filter(Boolean)}
                @meaning-grammeme-created=${this.refreshMeaningGrammemes}
              ></meaning-grammeme-form>
            </section>
          ` : ''}

          <div class="form-actions">
            <md-outlined-button type="button" @click=${this.handleCancel}>${t('createDialog.cancel')}</md-outlined-button>
            <md-filled-button 
              type="submit"
              data-testid="create-submit"
              ?disabled=${this.creating || this.loading}
            >
              ${this.creating ? t('createDialog.creating') : t('createDialog.create')}
            </md-filled-button>
          </div>
        </form>

        <section class="recent-entries-section" aria-label="${t('createDialog.recentEntries') || 'Latest entries'}">
          <h2 class="recent-entries-title">${t('createDialog.recentEntries') || 'Latest entries'}</h2>
          <word-entries-list
            .groups=${this.recentEntries}
            .columns=${this.recentEntriesColumns}
            .languages=${this.languages}
            .meaningTypes=${this.meaningTypes}
            showHeader
            headerLabel="${t('createDialog.recentEntries') || 'Latest entries'}"
            ?loading=${this.recentEntriesLoading}
            ?flexColumns=${true}
            widerColumnLanguageId="1"
          ></word-entries-list>
        </section>
      </div>
    `
  }

  private get recentEntriesColumns(): ColumnConfig[] {
    return this.languages.map((lang) => ({
      languageId: lang.id,
      meaningTypeIds: [],
      spellingSystemIds: [],
    }))
  }

  private renderSpellingsBlock() {
    return html`
      <div class="spellings-block">
        <span class="block-label">${t('createDialog.spelling')} *</span>
        ${this.spellings.map((row) => html`
          <div class="spelling-row">
            <md-filled-select
              label=${t('createDialog.spellingSystem')}
              data-testid=${row.id === this.spellings[0]?.id ? 'create-spelling-system' : undefined}
              .value=${row.systemId}
              @change=${(e: Event) => { this.updateSpellingRow(row.id, 'systemId', (e.target as any).value); }}
            >
              ${this.spellingSystems.map((s) => html`
                <md-select-option value=${s.id}><div slot="headline">${t(s.name)}</div></md-select-option>
              `)}
            </md-filled-select>
            <md-filled-text-field
              label=${t('createDialog.spelling')}
              .value=${row.text}
              data-testid=${row.id === this.spellings[0]?.id ? 'create-spelling-input' : undefined}
              @input=${(e: Event) => { this.updateSpellingRow(row.id, 'text', (e.target as HTMLInputElement).value); }}
              placeholder=${t('createDialog.spellingPlaceholder')}
            ></md-filled-text-field>
            ${this.spellings.length > 1
              ? html`<md-outlined-button type="button" class="row-remove" @click=${() => this.removeSpellingRow(row.id)}>${t('common.delete')}</md-outlined-button>`
              : ''}
          </div>
        `)}
        <md-outlined-button type="button" @click=${() => this.addSpellingRow()}>${t('createDialog.addSpelling')}</md-outlined-button>
      </div>
    `
  }

  private renderEtymologyBlock() {
    return html`
      <div class="etymology-block">
        <span class="block-label">${t('createDialog.etymologyRoots')}</span>
        <span class="block-hint">${t('createDialog.etymologyRootsHint')}</span>
        ${this.etymologyRoots.length > 0 ? html`
          <div class="etymology-selected">
            ${this.etymologyRoots.map((root, index) => html`
              <etymology-root-display
                .root=${{
                  id: root.id,
                  typeName: root.typeName ?? '',
                  languageName: root.languageName ?? '',
                  displaySpelling: root.displaySpelling ?? '',
                }}
                removable
                @remove=${() => this.removeEtymologyRoot(index)}
              ></etymology-root-display>
            `)}
          </div>
        ` : ''}
        <div class="etymology-search">
          <md-filled-select
            label=${t('createDialog.language')}
            .value=${this.etymologySearchLanguageId}
            data-testid="create-etymology-language"
            @change=${(e: Event) => { this.etymologySearchLanguageId = (e.target as any).value; this.searchEtymologyMeanings(0); }}
          >
            <md-select-option value=""><div slot="headline">—</div></md-select-option>
            ${this.languages.map(lang => html`
              <md-select-option value=${lang.id}><div slot="headline">${t(lang.name)}</div></md-select-option>
            `)}
          </md-filled-select>
          <md-filled-text-field
            label=${t('createDialog.selectMeaning')}
            .value=${this.etymologySearchFilter}
            data-testid="create-etymology-search"
            placeholder=${t('createDialog.etymologySearchPlaceholder')}
            @input=${(e: Event) => { this.etymologySearchFilter = (e.target as HTMLInputElement).value; this.onEtymologySearchInput(); }}
          ></md-filled-text-field>
          ${this.etymologySearchLoading ? html`<div class="loading meanings-loading">${t('common.loading')}</div>` : ''}
          ${!this.etymologySearchLoading && this.etymologySearchResults.length > 0 ? html`
            <ul class="etymology-results">
              ${this.etymologySearchResults
                .filter((item) => !this.etymologyRoots.some((r) => r.id === item.meaning.id))
                .map((item) => html`
                <li>
                  <button type="button" class="etymology-result-btn" data-testid="create-etymology-result" @click=${() => this.addEtymologyRoot(item)}>
                    ${item.primarySpelling ? `${item.primarySpelling} · ` : ''}${t(item.meaning.type.name)} · ${t(item.meaning.language.name)} (${item.meaning.word.id})${item.displaySpelling ? ` — ${item.displaySpelling}` : ''}
                  </button>
                </li>
              `)}
            </ul>
            <div class="etymology-pagination">
              ${this.etymologySearchPage > 0 ? html`
                <md-outlined-button type="button" @click=${() => this.searchEtymologyMeanings(this.etymologySearchPage - 1)}>${t('wordView.pagination.previous')}</md-outlined-button>
              ` : ''}
              <span class="etymology-pagination-label">${t('createDialog.etymologyPageLabel', { page: String(this.etymologySearchPage + 1) })}</span>
              ${this.etymologySearchResults.length === CreateEntryPage.ETYMOLOGY_SEARCH_PAGE_SIZE ? html`
                <md-outlined-button type="button" @click=${() => this.searchEtymologyMeanings(this.etymologySearchPage + 1)}>${t('wordView.pagination.next')}</md-outlined-button>
              ` : ''}
            </div>
          ` : ''}
        </div>
      </div>
    `
  }

  private renderWordMeaningSpellingForm() {
    return html`
      <md-filled-select
        label=${t('createDialog.meaningType')}
        data-testid="create-meaning-type"
        .value=${this.selectedMeaningTypeId}
        @change=${(e: Event) => { this.selectedMeaningTypeId = (e.target as any).value }}
        @input=${(e: Event) => { this.selectedMeaningTypeId = (e.target as any).value }}
        required
      >
        ${this.meaningTypes.map(type => html`
          <md-select-option value=${type.id}><div slot="headline">${t(type.name)}</div></md-select-option>
        `)}
      </md-filled-select>

      <md-filled-select
        label=${t('createDialog.language')}
        .value=${this.selectedLanguageId}
        data-testid="create-language"
        @change=${(e: Event) => { this.selectedLanguageId = (e.target as any).value }}
        @input=${(e: Event) => { this.selectedLanguageId = (e.target as any).value }}
        required
      >
        ${this.languages.map(lang => html`
          <md-select-option value=${lang.id}><div slot="headline">${t(lang.name)}</div></md-select-option>
        `)}
      </md-filled-select>

      ${this.renderEtymologyBlock()}
      ${this.renderSpellingsBlock()}
    `
  }

  private renderMeaningSpellingForm() {
    return html`
      <div class="word-search-block">
        <span class="block-label">${t('createDialog.word')} *</span>
        ${this.selectedWordFromSearch ? html`
          <etymology-root-display
            .root=${{
              id: this.selectedWordFromSearch.meaning.word.id,
              typeName: this.selectedWordFromSearch.meaning.type.name ?? '',
              languageName: this.selectedWordFromSearch.meaning.language.name ?? '',
              displaySpelling: this.selectedWordFromSearch.displaySpelling ?? this.selectedWordFromSearch.primarySpelling ?? '',
            }}
            labelType="word"
            removable
            @remove=${() => this.clearSelectedWord()}
          ></etymology-root-display>
        ` : html`
          <div class="etymology-search">
            <md-filled-select
              label=${t('createDialog.language')}
              .value=${this.wordSearchLanguageId}
              @change=${(e: Event) => { this.wordSearchLanguageId = (e.target as any).value; this.searchWordMeanings(0); }}
              data-testid="create-word-search-language"
            >
              <md-select-option value=""><div slot="headline">—</div></md-select-option>
              ${this.languages.map(lang => html`
                <md-select-option value=${lang.id}><div slot="headline">${t(lang.name)}</div></md-select-option>
              `)}
            </md-filled-select>
            <md-filled-text-field
              label=${t('createDialog.selectMeaning')}
              .value=${this.wordSearchFilter}
              data-testid="create-word-search-filter"
              placeholder=${t('createDialog.etymologySearchPlaceholder')}
              @input=${(e: Event) => { this.wordSearchFilter = (e.target as HTMLInputElement).value; this.onWordSearchInput(); }}
            ></md-filled-text-field>
            ${this.wordSearchLoading ? html`<div class="loading meanings-loading">${t('common.loading')}</div>` : ''}
            ${!this.wordSearchLoading && this.wordSearchResults.length > 0 ? html`
              <ul class="etymology-results">
                ${this.wordSearchResults.map((item) => html`
                  <li>
                    <button type="button" class="etymology-result-btn" data-testid="create-word-search-result" @click=${() => this.selectWordFromSearch(item)}>
                      ${item.primarySpelling ? `${item.primarySpelling} · ` : ''}${item.meaning.type.name ? t(item.meaning.type.name) : ''} · ${item.meaning.language.name ? t(item.meaning.language.name) : ''} (Word:${item.meaning.word.id}) (Meaning:${item.meaning.id}) ${item.displaySpelling ? ` — ${item.displaySpelling}` : ''}
                    </button>
                  </li>
                `)}
              </ul>
              <div class="etymology-pagination">
                ${this.wordSearchPage > 0 ? html`
                  <md-outlined-button type="button" @click=${() => this.searchWordMeanings(this.wordSearchPage - 1)}>${t('wordView.pagination.previous')}</md-outlined-button>
                ` : ''}
                <span class="etymology-pagination-label">${t('createDialog.etymologyPageLabel', { page: String(this.wordSearchPage + 1) })}</span>
                ${this.wordSearchResults.length === CreateEntryPage.ETYMOLOGY_SEARCH_PAGE_SIZE ? html`
                  <md-outlined-button type="button" @click=${() => this.searchWordMeanings(this.wordSearchPage + 1)}>${t('wordView.pagination.next')}</md-outlined-button>
                ` : ''}
              </div>
            ` : ''}
          </div>
        `}
      </div>

      <md-filled-select
        label=${t('createDialog.meaningType')}
        .value=${this.selectedMeaningTypeId}
        @change=${(e: Event) => { this.selectedMeaningTypeId = (e.target as any).value }}
        @input=${(e: Event) => { this.selectedMeaningTypeId = (e.target as any).value }}
        data-testid="create-meaning-type"
        required
      >
        ${this.meaningTypes.map(type => html`
          <md-select-option value=${type.id}><div slot="headline">${t(type.name)}</div></md-select-option>
        `)}
      </md-filled-select>

      <md-filled-select
        label=${t('createDialog.language')}
        .value=${this.selectedLanguageId}
        @change=${(e: Event) => { this.selectedLanguageId = (e.target as any).value }}
        @input=${(e: Event) => { this.selectedLanguageId = (e.target as any).value }}
        data-testid="create-language"
        required
      >
        ${this.languages.map(lang => html`
          <md-select-option value=${lang.id}><div slot="headline">${t(lang.name)}</div></md-select-option>
        `)}
      </md-filled-select>

      ${this.renderEtymologyBlock()}
      ${this.renderSpellingsBlock()}
    `
  }

  private renderSpellingForm() {
    return html`
      <div class="word-search-block">
        <span class="block-label">${t('createDialog.selectMeaning')} *</span>
        ${this.selectedMeaningFromSearch ? html`
          <etymology-root-display
            .root=${{
              id: this.selectedMeaningFromSearch.meaning.id,
              typeName: this.selectedMeaningFromSearch.meaning.type.name ?? '',
              languageName: this.selectedMeaningFromSearch.meaning.language.name ?? '',
              displaySpelling: this.selectedMeaningFromSearch.displaySpelling ?? this.selectedMeaningFromSearch.primarySpelling ?? '',
            }}
            removable
            @remove=${() => this.clearSelectedMeaning()}
          ></etymology-root-display>
        ` : html`
          <div class="etymology-search">
            <md-filled-select
              label=${t('createDialog.language')}
              .value=${this.spellingSearchLanguageId}
              data-testid="create-spelling-search-language"
              @change=${(e: Event) => { this.spellingSearchLanguageId = (e.target as any).value; this.searchMeaningsForSpelling(0); }}
            >
              <md-select-option value=""><div slot="headline">—</div></md-select-option>
              ${this.languages.map(lang => html`
                <md-select-option value=${lang.id}><div slot="headline">${t(lang.name)}</div></md-select-option>
              `)}
            </md-filled-select>
            <md-filled-text-field
              label=${t('createDialog.selectMeaning')}
              .value=${this.spellingSearchFilter}
              data-testid="create-spelling-search-filter"
              placeholder=${t('createDialog.etymologySearchPlaceholder')}
              @input=${(e: Event) => { this.spellingSearchFilter = (e.target as HTMLInputElement).value; this.onSpellingSearchInput(); }}
            ></md-filled-text-field>
            ${this.spellingSearchLoading ? html`<div class="loading meanings-loading">${t('common.loading')}</div>` : ''}
            ${!this.spellingSearchLoading && this.spellingSearchResults.length > 0 ? html`
              <ul class="etymology-results">
                ${this.spellingSearchResults.map((item) => html`
                  <li>
                    <button type="button" class="etymology-result-btn" data-testid="create-spelling-search-result" @click=${() => this.selectMeaningFromSearch(item)}>
                      ${item.primarySpelling ? `${item.primarySpelling} · ` : ''}${item.meaning.type.name ? t(item.meaning.type.name) : ''} · ${item.meaning.language.name ? t(item.meaning.language.name) : ''} (Meaning:${item.meaning.id}) ${item.displaySpelling ? ` — ${item.displaySpelling}` : ''}
                    </button>
                  </li>
                `)}
              </ul>
              <div class="etymology-pagination">
                ${this.spellingSearchPage > 0 ? html`
                  <md-outlined-button type="button" @click=${() => this.searchMeaningsForSpelling(this.spellingSearchPage - 1)}>${t('wordView.pagination.previous')}</md-outlined-button>
                ` : ''}
                <span class="etymology-pagination-label">${t('createDialog.etymologyPageLabel', { page: String(this.spellingSearchPage + 1) })}</span>
                ${this.spellingSearchResults.length === CreateEntryPage.ETYMOLOGY_SEARCH_PAGE_SIZE ? html`
                  <md-outlined-button type="button" @click=${() => this.searchMeaningsForSpelling(this.spellingSearchPage + 1)}>${t('wordView.pagination.next')}</md-outlined-button>
                ` : ''}
              </div>
            ` : ''}
          </div>
        `}
      </div>

      ${this.renderSpellingsBlock()}
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'create-entry-page': CreateEntryPage
  }
}
